// @flow

import * as React from 'react';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';
import times from 'lodash/times';
import sample from 'lodash/sample';
import type { LetterPoolSolution, LetterPoolChallenge, ChallengeStyle } from '../../lib/Challenge';
import JokerHat from './letterpool/JokerHat';
import Lightbulb from './letterpool/Lightbulb';
import type { statisticsHelper } from '../lib/Challenge';


// each letter is represented by a one-letter string.
// empty letters are represented with `null`.


type Props = {
  isPortrait: boolean,
  imageSize: { width: number, height: number },
  buttonColors: any,
  onComplete: (() => void),
  onWrongSolution: (() => void),
  onHintRequest: (() => void),
  challenge: LetterPoolChallenge,
  className: string,
  showsHint: boolean,
  hideToolbar: boolean,
  style: ChallengeStyle,
  statisticsHelper: statisticsHelper,
  decreaseJokerCount: (() => void),
}

type Letter = {
  string: string,
  isInSolution: boolean,
  index: number,
};

type State = {
  letters: Letter[],
  isJokerDisabled: boolean,
};

type IndexedLetter = {
  index: number,
  letter: Letter,
};

const NumberOfLettersInLetterPool = 14;


const LetterElement = styled.button`
  display: inline-block;
`;

const RemainingPlaceholder = styled.span.attrs({ className: 'placeholder' })``;

function Circle(props: { className: string, id: string }) {
  return <span id={props.id} className={props.className}><span className="circle"></span></span>;
}

const SolutionPlaceholder = styled(Circle)`
  display: inline-block;
  .circle {
    background-color: ${props => props.color};
    width: 1em;
    height: 1em;
    border-radius: 0.5em;
    opacity: 0.4;
  }
`;


function asLetterString(letter: any): string | null {
  if (typeof letter === 'string') {
    if (letter.length === 1) return letter;
    return letter[0];
  }
  return null;
}


// guarantees that the output is an array of single letters or `null` values in correct length
function cleanupSolutionAttempt(correctSolution: LetterPoolSolution, attempt: LetterPoolSolution): LetterPoolSolution {
  const attemptAsArray = attempt instanceof Array ? attempt : [];
  return times(correctSolution.length, () => null)
    .map((letter, index) => {
      if (correctSolution[index] === ' ') {
        return ' '; // If the solution contains a space, pre-insert it in the attempt
      }
      return asLetterString(attemptAsArray[index]);
    });
}


class LetterPool extends React.Component<Props, State> {
  props: Props;
  state = { letters: [], isJokerDisabled: this.props.statisticsHelper.jokerCount <= 0 ? true : false };


  componentWillMount() {
    const presolvedSolution = cleanupSolutionAttempt(this.correctSolution(), this.props.challenge.preSolved);

    // Create letter objects from the presolved characters array
    const presolvedLetters: Letter[] = presolvedSolution
      .map((string, index) => {
        if (string === null) { return null; }
        return { string, index, isInSolution: true };
      })
      .filter(Boolean);

    // Create letter objects from the remaining characters array.
    // Spaces are automatically pre-solved, so don't include them as buttons.
    const remainingLetters: Letter[] = (this.props.challenge.letterPool || [])
      .map(asLetterString)
      .filter(letterString => letterString !== ' ')
      .map((string, index) => {
        if (string === null) { return null; }
        return { string, index, isInSolution: false };
      })
      .filter(Boolean);

    this.setState({ letters: [].concat(presolvedLetters, remainingLetters) });
  }


  updateLetterLayout = () => {
    this.setState({ letters: [].concat(this.state.letters) });
    setTimeout(() => { this.setState({ letters: [].concat(this.state.letters) }); }, 100);
  }


  componentDidMount() {
    this.updateLetterLayout();
    window.letterpoolQuiz = this;

    window.addEventListener('resize', this.updateLetterLayout);
    window.addEventListener('orientationChange', this.updateLetterLayout);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateLetterLayout);
    window.removeEventListener('orientationChange', this.updateLetterLayout);
  }


  render() {
    const { isPortrait, imageSize, style } = this.props;
    const css = {
      height: isPortrait ? '' : imageSize.height,
      background: style.background.forTextContent,
      color: style.textColor,
    };
    return (<div className={`${this.props.className}`} style={css}>
      <header className="question">
        {this.props.challenge.question}
      </header>

      <section className={`toolbar ${this.props.hideToolbar ? 'hidden' : ''}`}>
        <button className="hint-button" onClick={() => this.props.onHintRequest()} disabled={this.props.showsHint}>
          <Lightbulb />
        </button>

        <button onClick={() => this.helpThePoorPersonSolvingThisDifficultPuzzle()} disabled={this.state.isJokerDisabled}>
          <JokerHat />
        </button>
      </section>

      <div className="letterpool-container">
        <section className="attempted">
          {this.correctSolution().map((letter, index) => (<SolutionPlaceholder color={style.primaryColor} className={this.hasSolutionLetterAtIndex(index) ? 'hidden' : ''} key={index} id={`solution-${index}`} />))}
        </section>

        <footer className="remaining">
          {times(14).map((index) => (<RemainingPlaceholder key={index} id={`remaining-${index}`} />))}
        </footer>

        <div className="letters">
          {this.state.letters.map((letter: Letter, index: number) => {
            const classNames = [
              letter.isInSolution ? 'is-in-solution' : null,
            ].filter(Boolean);

            return (<LetterElement
              key={index}
              className={classNames.join(' ')}
              onClick={() => this.letterClicked(letter)}
              style={this.styleForLetter(letter)}
            >
              {letter.string}
            </LetterElement>);
          })}
        </div>
      </div>
    </div>);
  }


  letterClicked(letter: Letter) {
    if (letter.isInSolution) {
      // const isLetterCorrect = this.correctSolution()[letter.index] === letter.string;
      // if (isLetterCorrect) return;
      // Don't allow to remove spaces
      if (letter.string === ' ') return;
      this.removeLetterFromSolution(letter);
    } else {
      const nextEmptySolutionIndex = this.currentSolutionAttempt().indexOf(null);
      if (nextEmptySolutionIndex === -1) {
        // No space left in solution
        return;
      }
      this.moveLetterToSolution(letter, nextEmptySolutionIndex);
    }

    this.updateLetterLayout();
    this.checkCompletion();
  }


  styleForLetter(letter): $Shape<CSSStyleDeclaration> {
    const element = document.querySelector(`#${letter.isInSolution ? 'solution' : 'remaining'}-${letter.index}`);
    if (!element) return { display: 'none' };
    return {
      width: String(element.offsetWidth + 1),
      height: String(element.offsetHeight),
      transform: `translate3d(${element.offsetLeft}px, ${element.offsetTop}px, 0px)`,
      WebkitTransform: `translate3d(${element.offsetLeft}px, ${element.offsetTop}px, 0px)`,
      MozTransform: `translate3d(${element.offsetLeft}px, ${element.offsetTop}px, 0px)`,
    };
  }


  removeLetterFromSolution(letter: Letter) {
    if (!letter.isInSolution) return;
    letter.index = this.currentLetterPool().indexOf(null);
    letter.isInSolution = false;
  }


  moveLetterToSolution(letter: Letter, atIndex: number) {
    if (letter.isInSolution) return;
    letter.index = atIndex;
    letter.isInSolution = true;
  }


  currentSolutionAttempt(): (Letter | null)[] {
    const result = [].concat(this.correctSolution()).fill(null);
    this.state.letters.filter(l => l.isInSolution).forEach(l => result[l.index] = l);
    return result;
  }


  hasSolutionLetterAtIndex(index: number): boolean {
    return this.currentSolutionAttempt()[index] !== null;
  }


  currentLetterPool(): (Letter | null)[] {
    const result = Array(NumberOfLettersInLetterPool).fill(null);
    this.state.letters.filter(l => !l.isInSolution).forEach(l => result[l.index] = l);
    return result;
  }


  wrongLetters(): Letter[] {
    return this.state.letters
      .filter(l => l.isInSolution && this.correctSolution()[l.index] !== l.string);
  }


  missingCorrectLetters(): IndexedLetter[] {
    const solution = this.correctSolution();
    return this.currentSolutionAttempt().map((letter, index) => {
      if (letter) return null;
      const missingLetter: ?Letter = this.state.letters.find(l => !l.isInSolution && l.string === solution[index]);
      if (!missingLetter) return null;
      return { index, letter: missingLetter };
    }).filter(Boolean);
  }


  removeWrongLetters() {
    this.wrongLetters().forEach(l => this.removeLetterFromSolution(l));
  }


  helpThePoorPersonSolvingThisDifficultPuzzle() {

    this.removeWrongLetters();
    const missingIndexedLetter = sample(this.missingCorrectLetters());
    if (!missingIndexedLetter) return;
    this.moveLetterToSolution(missingIndexedLetter.letter, missingIndexedLetter.index);
    this.updateLetterLayout();
    if (!this.checkCompletion()) {
      this.setState({ isJokerDisabled: true });
      setTimeout(() => { this.setState({ isJokerDisabled: false }); }, 500);
    }

    if(!isNaN(this.props.statisticsHelper.jokerCount)){
      if(this.props.statisticsHelper.jokerCount -1 === 0){
        setTimeout(() => { this.setState({ isJokerDisabled: true }); }, 500);
      }
      if(this.props.statisticsHelper.jokerCount > 0){
        this.props.decreaseJokerCount();
      }
    }

  }


  checkCompletion(): boolean {
    const solutionAsStringArray = this.currentSolutionAttempt().map(l => l ? l.string : null);

    if (this.correctSolution().length !== solutionAsStringArray.filter(Boolean).length) {
      return false;
    }

    if (this.isSolutionCorrect(solutionAsStringArray)) {
      this.props.onComplete();
      return true;
    } else {
      this.removeWrongLetters();
      this.props.onWrongSolution();
      return false;
    }
  }


  isSolutionCorrect(solution: LetterPoolSolution) {
    return isEqual(solution, this.correctSolution());
  }


  correctSolution(): LetterPoolSolution {
    return this.props.challenge.correctAnswerArray;
  }
}


export default styled(LetterPool)`
  flex: 1 0.5 auto;

  display: flex;
  flex-direction: column;
  padding: 0;
  color: #111;
  background-color: white;

  > * {
    flex: 1;
  }

  .question {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1.5;
  }

  @keyframes highlightBackground {
    0% {
      background-color: ${props => props.style ? props.style.primaryColor : 'black'};
    },
    100% {
      background-color: transparent;
    }
  }

  .letterpool-container {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 3.5;
    .letters > * {
      position: absolute;
      top: 0;
      left: 0;
      transition: transform 0.15s ease-out, width 0.15s ease-out, height 0.15s ease-out;
      cursor: pointer;
      color: ${props => props.style ? props.style.primaryColor : 'black'};
      font-weight: bold;
      font-size: 1.25em;
      outline: none;
      border-radius: 0;
      border: none;
      outline: none;
      background-color: white;
      display: block;
      text-align: center;
      &.is-in-solution {
        animation: highlightBackground 0.3s ease-out;
        background: transparent;
      }
    }
  }

  .attempted, .remaining {
    display: flex;
    flex-direction: row;
    align-items: stretch;

    > * {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
  }

  .attempted {
    flex: 1.5;
    justify-content: space-around;
    box-pack: justify;
    padding: 0 30px;
    display: -webkit-box;
    -webkit-box-pack: justify;

    > * {
      width: 7.1428571%; /* maximal 14 chars per line */
      height: 3em;
      background: transparent;
      transition: transform 1s ease-out, opacity 1s ease-out;
      transform: scale3d(1, 1, 1);
      opacity: 1;
      &.hidden {
        transform: scale3d(2, 2, 2);
        opacity: 0;
      }
    }
  }

  .remaining {
    justify-content: center;
    flex: 2;
    flex-wrap: wrap;
    > * {
      width: 14.2857143%; /* maximal 7 characters per line */
      background: radial-gradient(circle at center, #fff, #eee);
      &.placeholder {
        background: radial-gradient(circle at center, #eee, #ddd);
      }
    }
  }

  .toolbar {
    position: absolute;
    right: 0;
    top: 30%;
    @media ( orientation: landscape ) {
      top: auto;
      bottom: 20px;
    }
    display: flex;
    flex-direction: column;
    background: transparent;
    transition: transform 0.25s ease-out, opacity 0.25s ease-out;
    transform: translate3d(0, 0, 0);
    &.hidden {
      opacity: 0;
      pointer-events: none;
      transform: translate3d(50px, 0, 0);
    }

    > * {
      flex: 1;
      font-size: 1em;
      width: 50px;
      height: 50px;
      outline: none;
      border-radius: 0;
      border: none;
      color: white !important;
      background: linear-gradient(to right, ${props => props.style ? props.style.secondaryColor : 'black'}, ${props => props.style ? props.style.secondaryColor : 'black'});
      padding: 1px;
      cursor: pointer;
      transition: opacity 0.1s ease-out, transform 0.1s ease-out;
      margin: 5px;
      margin-right: 0;
      box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
      &:disabled {
        opacity: 0;
        &.hint-button {
          opacity: 0;
        }
      }
      &:active {
        transform: scale3d(0.9, 0.9);
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/campaign/challenges/quiz/letterpool.jsx